import { Editor, defaultValueCtx, editorViewCtx, rootCtx } from '@milkdown/kit/core';
import { commonmark } from '@milkdown/kit/preset/commonmark';
import { gfm } from '@milkdown/kit/preset/gfm';
import { history } from '@milkdown/kit/plugin/history';
import { trailing } from '@milkdown/kit/plugin/trailing';
import { upload } from '@milkdown/kit/plugin/upload';
import { collab } from '@milkdown/plugin-collab';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';
import { $prose } from '@milkdown/kit/utils';
import { replaceAll } from '@milkdown/kit/utils';
import {
    Milkdown,
    MilkdownProvider,
    useEditor,
    useInstance
} from '@milkdown/react';
import {
    ProsemirrorAdapterProvider,
    useNodeViewFactory
} from '@prosemirror-adapter/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Textarea, useComputedColorScheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { IconCalculator, IconShare } from '@tabler/icons-react';

import { DocumentDto } from '../../../api/models';
import { useGetApiProductiveServiceNames } from '../../../api/productive/productive';
import { useIdentityContext } from '../../auth/IdentityContext';
import { encodeBlockData, isDecodableBlockData } from '../core/data-encoding';
import { createDefaultCostEstimateData } from '../directives/utils/costEstimateUtils';
import { EditorAdapterProvider } from '../core/EditorAdapterContext';
import { migrateJsxToDirective } from '../core/migrate-jsx-to-directive';
import type { BlockTypeValue } from '../core/types';
import { getAllBlockPlugins } from '../core/BlockPluginRegistry';
import { DocumentProvider } from '../directives/contexts/DocumentContext';

// Block plugin registration (side-effect import)
import '../blocks';

import { createDirectivePlugins } from './directive-node-factory';
import { configureImageUpload } from './image-upload-plugin';
import type { MilkdownAdapter } from './milkdown-adapter';
import MilkdownToolbar from './MilkdownToolbar';
import { type CollabConfig, type CollabConnection, type CollabStatus, connectCollab } from './collab-plugin';
import { remarkDirectivePlugin, remarkStripTextDirectivesPlugin } from './remark-directive-plugin';
import { slashMenu, useSlashMenu, setExtraSlashItems, type SlashItem } from './slash-menu';
import { formattingTooltip, useFormattingTooltip } from './tooltip-plugin';
import { tableTooltip, useTableToolbar } from './table-toolbar';

import '@milkdown/kit/prose/view/style/prosemirror.css';
import './milkdown-editor.css';
import './collab-cursors.css';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface MilkdownEditorProps {
    data: DocumentDto;
    adapter: MilkdownAdapter;
    saveDoc: (documentContents: string) => Promise<void>;
    exportDocX: (documentContents: string) => void;
    openBpmnGenerator: () => void;
    onContentChange?: () => void;
    readOnly?: boolean;
    /** Required for editable documents. Optional for readOnly (falls back to local history). */
    collabConfig?: CollabConfig;
    /** Called when collab connection status changes (e.g. for detecting external content updates). */
    onCollabStatusChange?: (status: CollabStatus) => void;
}

export type { CollabConfig, CollabStatus };

const AUTO_SAVE_DEBOUNCE_MS = 1500;
const AUTO_SAVE_RETRY_MS = 5000;
const AUTO_SAVE_MAX_RETRIES = 5;

/** Matches a leaf directive carrying a base64 data payload, e.g. ::costEstimate{data="..."} */
const DIRECTIVE_DATA_RE = /::([A-Za-z][\w-]*)\{[^}]*\bdata="([^"]*)"[^}]*\}/g;

/**
 * Returns the names of any directive blocks in the markdown whose data payload
 * can't be decoded. Applying such source would silently reset those blocks to
 * their defaults (directive-node-factory falls back on decode failure), so the
 * caller should warn rather than discard the user's content.
 */
function findCorruptDirectiveBlocks(markdown: string): string[] {
    const corrupt: string[] = [];
    for (const [, name, data] of markdown.matchAll(DIRECTIVE_DATA_RE)) {
        if (!isDecodableBlockData(data) && !corrupt.includes(name)) {
            corrupt.push(name);
        }
    }
    return corrupt;
}

/**
 * Inner editor component — must be rendered inside MilkdownProvider
 * and ProsemirrorAdapterProvider.
 */
function MilkdownEditorInner({
    adapter,
    migratedContent,
    readOnly,
    onDocChanged,
    collabConfig,
    onCollabStatusChange,
    onPeerCountChange
}: {
    adapter: MilkdownAdapter;
    migratedContent: string;
    readOnly?: boolean;
    onDocChanged: () => void;
    collabConfig?: CollabConfig;
    onCollabStatusChange?: (status: CollabStatus, reason?: string) => void;
    onPeerCountChange?: (count: number) => void;
}) {
    const nodeViewFactory = useNodeViewFactory();
    const configureTooltip = useFormattingTooltip();
    const configureSlash = useSlashMenu();
    const configureTable = useTableToolbar();
    const editorCreatedRef = useRef(false);
    const collabConnectionRef = useRef<CollabConnection | null>(null);

    // Create directive plugins for all registered block plugins
    const directivePlugins = useMemo(() => {
        const allPlugins = getAllBlockPlugins();
        return allPlugins.flatMap((plugin) =>
            createDirectivePlugins(plugin, nodeViewFactory)
        );
    }, [nodeViewFactory]);

    // Lightweight plugin that fires onDocChanged for auto-save/outline and
    // tracks the current block type for the toolbar dropdown.
    // Replaces Milkdown's listener plugin which filters addToHistory:false
    // transactions — blocking ALL doc-change events in collab mode.
    const docChangeAndSelectionPlugin = useMemo(
        () =>
            $prose(() => {
                let prevDoc: import('@milkdown/kit/prose/model').Node | null = null;
                return new Plugin({
                    key: new PluginKey('doc-change-and-selection'),
                    view: () => ({
                        update: (view: EditorView) => {
                            const { state } = view;

                            // Notify doc changes (drives auto-save + outline refresh)
                            if (prevDoc && !prevDoc.eq(state.doc)) {
                                onDocChanged();
                            }
                            prevDoc = state.doc;

                            // Track current block type for the toolbar dropdown
                            const { $from } = state.selection;
                            const parent = $from.parent;

                            let blockType: BlockTypeValue = 'paragraph';
                            if (parent.type.name === 'heading') {
                                const level = parent.attrs.level as number;
                                blockType = `h${level}` as BlockTypeValue;
                            } else if (parent.type.name === 'blockquote') {
                                blockType = 'quote';
                            } else if (parent.type.name === 'code_block') {
                                blockType = 'code';
                            }

                            adapter.updateCurrentBlockType(blockType);
                        }
                    })
                });
            }),
        [adapter, onDocChanged]
    );

    useEditor((root) => {
        const editor = Editor.make()
            .config((ctx) => {
                ctx.set(rootCtx, root);
                ctx.set(defaultValueCtx, migratedContent);

                // Configure image upload
                configureImageUpload(ctx);

                // Configure floating formatting tooltip
                if (!readOnly) {
                    configureTooltip(ctx);
                    configureSlash(ctx);
                    configureTable(ctx);
                }
            })
            .use(commonmark)
            .use(gfm)
            .use(collabConfig ? collab : history)
            .use(upload)
            .use(remarkDirectivePlugin)
            .use(remarkStripTextDirectivesPlugin)
            .use(directivePlugins)
            .use(docChangeAndSelectionPlugin)
            .use(trailing)
            .use(formattingTooltip)
            .use(slashMenu)
            .use(tableTooltip);

        // Wire adapter and collab after editor creation
        void editor.create().then((editorInstance) => {
            adapter.wireEditor(editorInstance);
            editorCreatedRef.current = true;

            if (readOnly) {
                editorInstance.action((ctx) => {
                    const view = ctx.get(editorViewCtx);
                    view.setProps({ editable: () => false });
                });
            }

            // Wire Yjs collaboration if configured
            if (collabConfig) {
                editorInstance.action((ctx) => {
                    // Destroy any leaked connection from a previous editor creation
                    // (safety net for editor recreation without component unmount)
                    collabConnectionRef.current?.destroy();
                    collabConnectionRef.current = null;

                    const connection = connectCollab(
                        ctx,
                        collabConfig,
                        (status, reason) => onCollabStatusChange?.(status, reason),
                    );
                    collabConnectionRef.current = connection;

                    if (onPeerCountChange) {
                        connection.onPeerCountChange(onPeerCountChange);
                    }
                });
            }
        });

        return editor;
    }, [migratedContent, readOnly]);

    // Clean up collab connection on unmount
    useEffect(() => {
        return () => {
            collabConnectionRef.current?.destroy();
            collabConnectionRef.current = null;
        };
    }, []);

    // Update content when data changes externally (non-collab only).
    // In collab mode, Yjs is the single source of truth — replaceAll would
    // blow away remote users' changes and desync the CRDT state.
    const [loading, getInstance] = useInstance();
    const prevContentRef = useRef(migratedContent);

    useEffect(() => {
        if (collabConfig) return; // Yjs is the source of truth
        if (loading || !editorCreatedRef.current) return;
        if (migratedContent === prevContentRef.current) return;
        prevContentRef.current = migratedContent;

        const editor = getInstance();
        if (editor) {
            editor.action(replaceAll(migratedContent));
        }
    }, [migratedContent, loading, getInstance, collabConfig]);

    return <Milkdown />;
}

/**
 * Top-level Milkdown editor component.
 * Wraps the editor with all required providers.
 * Always connects to Hocuspocus and auto-saves markdown on a 3s debounce.
 */
const MilkdownEditor = ({
    data,
    adapter,
    saveDoc,
    exportDocX,
    openBpmnGenerator,
    onContentChange,
    readOnly,
    collabConfig,
    onCollabStatusChange: onCollabStatusChangeProp,
}: MilkdownEditorProps) => {
    const dark = useComputedColorScheme('dark') === 'dark';
    const userIdentity = useIdentityContext();

    const [collabStatus, setCollabStatus] = useState<CollabStatus>('connecting');
    const [peerCount, setPeerCount] = useState(0);
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
    const [sourceView, setSourceView] = useState(false);
    const [sourceText, setSourceText] = useState('');

    const toggleSourceView = useCallback(() => {
        if (!sourceView) {
            // Entering source view — snapshot current markdown. Disallowed
            // while peers are connected: toggle-back whole-doc-replaces the
            // document and would clobber their concurrent edits (the button is
            // already disabled in that case; this guards the race).
            if (peerCount > 0) return;
            setSourceText(adapter.getMarkdown());
            setSourceView(true);
            return;
        }

        // Leaving source view — validate before applying. A corrupted directive
        // payload would silently reset the block to defaults, and a parse
        // failure would discard the edit; in both cases keep the user in source
        // view with their text intact and tell them what's wrong.
        const corrupt = findCorruptDirectiveBlocks(sourceText);
        if (corrupt.length > 0) {
            notifications.show({
                title: 'Unreadable block data',
                message: `These block(s) have data that can't be decoded and would be reset if applied: ${corrupt.join(', ')}. Fix or remove them in the source first.`,
                color: 'red',
                autoClose: false,
            });
            return;
        }

        if (!adapter.setMarkdown(sourceText)) {
            notifications.show({
                title: "Couldn't apply markdown",
                message: 'The markdown source could not be parsed. Review it and try switching back again.',
                color: 'red',
                autoClose: false,
            });
            return;
        }

        setSourceView(false);
    }, [sourceView, sourceText, adapter, peerCount]);

    // If a peer joins while we're in source view, exit to the rich editor
    // immediately. We discard the unsynced source snapshot rather than applying
    // it — pushing it back would overwrite the peer's live edits via the CRDT.
    useEffect(() => {
        if (sourceView && peerCount > 0) {
            setSourceView(false);
            setSourceText('');
            notifications.show({
                title: 'Switched to rich text',
                message:
                    'Another editor joined this document. Raw-markdown editing is only available when you are the sole editor, so your unsaved source changes were discarded to protect their edits.',
                color: 'yellow',
            });
        }
    }, [peerCount, sourceView]);

    const migratedContent = useMemo(
        () => migrateJsxToDirective(data.content),
        [data.content]
    );

    // Register extra slash menu items (cost estimate, BPMN)
    const { data: serviceNames } = useGetApiProductiveServiceNames();

    useEffect(() => {
        if (readOnly) return;

        const items: SlashItem[] = [];

        if (serviceNames) {
            items.push({
                key: 'cost-estimate',
                label: 'Cost Estimate',
                icon: <IconCalculator size={18} />,
                group: 'Components',
                onRun: () => {
                    const productiveServices = serviceNames.map(
                        (service: { name: string }) => service.name
                    );
                    const defaultData = createDefaultCostEstimateData(productiveServices, undefined);
                    const base64Data = encodeBlockData(defaultData);

                    adapter.insertMarkdown(
                        `\n\nThe estimate provided below is a best-efforts approximation of the costs of the project based on the known requirements. This estimate is valid for 30 days from the date of this proposal. Actual costs may vary due to changes in project scope, client-caused delays, unforeseen circumstances, or other factors outside our control. During project execution and delivery, the Project Manager will notify the project stakeholders if factors affecting the timeline or cost arise and will strive to deliver the project in the expected time, scope and to the agreed budget.\n\nPrices exclude VAT.\n\n::costEstimate{data="${base64Data}"}\n\n**Resources**\n\nDeveloper(s): Provides technical input during workflow analysis, architecture planning, and UI wireframing preparation. Supports the identification of potential implementation challenges and contribute to risk assessments from a developer's perspective.\nTechnical Lead: Leads the technical advisory effort, ensuring that proposed solutions align with best practices and relevant standards. Reviews and advises on software architecture, risk planning, and requirements to support scalable and compliant development.\nProject Manager: Manages the project timeline, coordinates stakeholder sessions, and ensures clear communication and alignment across all parties. Responsible for tracking progress across activities and delivering consolidated outputs on schedule.\n\n`
                    );
                }
            });
        }

        items.push({
            key: 'bpmn-diagram',
            label: 'BPMN Diagram',
            icon: <IconShare size={18} />,
            group: 'Components',
            onRun: openBpmnGenerator
        });

        setExtraSlashItems(items);

        return () => setExtraSlashItems([]);
    }, [readOnly, serviceNames, adapter, openBpmnGenerator]);

    // Keep refs to the latest source-view state so unmount / page-unload
    // handlers (whose closures would otherwise capture stale values) persist
    // what the user is actually looking at.
    const sourceViewRef = useRef(sourceView);
    const sourceTextRef = useRef(sourceText);
    sourceViewRef.current = sourceView;
    sourceTextRef.current = sourceText;

    // The content to persist: in source view the textarea holds the edits (they
    // never reach the ProseMirror doc), so save those; otherwise serialize the
    // doc. Throws on serialization failure instead of masking it as '' (the
    // source-view string can't fail) so a failed save is surfaced and retried
    // rather than silently skipped — which previously left the status stuck on
    // "Saving..." while nothing was persisted.
    const getPersistableMarkdown = useCallback(
        (): string =>
            sourceViewRef.current
                ? sourceTextRef.current
                : adapter.getMarkdownStrict(),
        [adapter]
    );

    // Debounced auto-save with exponential backoff.
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const retryCountRef = useRef(0);
    const performAutoSaveRef = useRef<() => void>();
    // The markdown last successfully persisted. Used to dedupe so that
    // flush-on-hide/unmount and retries never re-send identical content (which
    // would spam saves and bump the document's LastUpdated for no reason).
    const lastSavedRef = useRef<string | null>(null);

    const clearAutoSaveTimer = useCallback(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = undefined;
        }
    }, []);

    // Surface a save failure (serialization OR network) and retry with
    // exponential backoff: 5s, 10s, 20s, 40s, 80s. After the final attempt,
    // warn the user with a persistent notification.
    const handleSaveFailure = useCallback(() => {
        setAutoSaveStatus('error');
        retryCountRef.current++;

        if (retryCountRef.current >= AUTO_SAVE_MAX_RETRIES) {
            notifications.show({
                title: 'Save Failed',
                message: 'Unable to save after multiple attempts. Please copy your work and reload the page.',
                color: 'red',
                autoClose: false,
            });
            return;
        }

        const delay = AUTO_SAVE_RETRY_MS * Math.pow(2, retryCountRef.current - 1);
        clearAutoSaveTimer();
        autoSaveTimerRef.current = setTimeout(() => performAutoSaveRef.current?.(), delay);
    }, [clearAutoSaveTimer]);

    // Canonical, state-aware save path: serialize, dedupe, persist, and manage
    // status + retry. A serialization failure is surfaced as an error — never
    // mistaken for an empty document and silently skipped.
    const performAutoSave = useCallback(
        async () => {
            let markdown: string;
            try {
                markdown = getPersistableMarkdown();
            } catch (err) {
                console.error('Auto-save: failed to serialize document', err);
                handleSaveFailure();
                return;
            }

            // Nothing meaningful to persist (don't blank the stored document),
            // or unchanged since the last successful save: report saved and stop.
            if (!markdown.trim() || markdown === lastSavedRef.current) {
                retryCountRef.current = 0;
                setAutoSaveStatus('saved');
                return;
            }

            setAutoSaveStatus('saving');
            try {
                await saveDoc(markdown);
                lastSavedRef.current = markdown;
                retryCountRef.current = 0;
                setAutoSaveStatus('saved');
            } catch (err) {
                console.error('Auto-save: save request failed', err);
                handleSaveFailure();
            }
        },
        [getPersistableMarkdown, saveDoc, handleSaveFailure]
    );
    performAutoSaveRef.current = performAutoSave;

    const scheduleAutoSave = useCallback(
        () => {
            setAutoSaveStatus('saving');
            retryCountRef.current = 0;
            clearAutoSaveTimer();
            autoSaveTimerRef.current = setTimeout(() => {
                autoSaveTimerRef.current = undefined;
                void performAutoSave();
            }, AUTO_SAVE_DEBOUNCE_MS);
        },
        [clearAutoSaveTimer, performAutoSave]
    );

    const contentChangeTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const handleDocChanged = useCallback(
        () => {
            if (!readOnly) {
                scheduleAutoSave();
            }

            // Debounce outline updates — no need to re-render on every character
            if (contentChangeTimerRef.current) clearTimeout(contentChangeTimerRef.current);
            contentChangeTimerRef.current = setTimeout(() => onContentChange?.(), 300);
        },
        [readOnly, scheduleAutoSave, onContentChange]
    );

    // Best-effort flush for teardown paths (unmount, page unload) where we must
    // not touch React state. Dedupes against the last save and skips an
    // unserializable/empty doc, so we never blank the stored document or
    // re-send unchanged content.
    const flushNow = useCallback(() => {
        clearAutoSaveTimer();
        let markdown: string;
        try {
            markdown = getPersistableMarkdown();
        } catch {
            return; // serialization failed — nothing safe to persist on the way out
        }
        if (!markdown.trim() || markdown === lastSavedRef.current) return;
        lastSavedRef.current = markdown;
        void saveDoc(markdown).catch(() => { /* best-effort */ });
    }, [clearAutoSaveTimer, getPersistableMarkdown, saveDoc]);

    // Flush on unmount so navigating away doesn't drop the last debounce window.
    useEffect(() => {
        return () => {
            if (contentChangeTimerRef.current) clearTimeout(contentChangeTimerRef.current);
            flushNow();
        };
    }, [flushNow]);

    // Source-view edits only update React state — the doc-change auto-save never
    // fires for them — so schedule a save whenever the textarea content changes.
    useEffect(() => {
        if (readOnly || !sourceView) return;
        scheduleAutoSave();
    }, [sourceText, sourceView, readOnly, scheduleAutoSave]);

    // Persist when the tab is hidden, closed, or discarded/slept. Background
    // tabs throttle or suspend timers and tab discard skips React cleanup, so
    // the debounce + unmount flush alone can silently drop the last edits — the
    // gap aggressive tab-sleeping browsers (and bfcache) expose.
    //
    // visibilitychange fires while the page is still alive (tab switch / minimise),
    // so we use the full state-aware save there. pagehide is the reliable
    // pre-unload/bfcache signal, where the state-free best-effort flush is used.
    // beforeunload is intentionally omitted: it harms bfcache eligibility and
    // its async save rarely completes on a real unload anyway.
    useEffect(() => {
        if (readOnly) return;
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'hidden') return;
            clearAutoSaveTimer();
            void performAutoSave();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', flushNow);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', flushNow);
        };
    }, [readOnly, clearAutoSaveTimer, performAutoSave, flushNow]);

    const handleCollabStatusChange = useCallback(
        (status: CollabStatus, reason?: string) => {
            setCollabStatus(status);
            onCollabStatusChangeProp?.(status);
            if (status === 'auth-failed') {
                notifications.show({
                    id: 'collab-error',
                    title: 'Collaboration Error',
                    message: `Authentication failed: ${reason ?? 'unknown reason'}. Your changes will not be saved. Export a copy of your work, then refresh the page.`,
                    color: 'red',
                    autoClose: false,
                });
            } else if (status === 'disconnected') {
                notifications.show({
                    id: 'collab-disconnect',
                    title: 'Collaboration Disconnected',
                    message: 'Lost connection to the collaboration server. Your changes will not be saved until the connection is restored. Attempting to reconnect...',
                    color: 'yellow',
                    autoClose: 5000,
                });
            } else if (status === 'connected') {
                // Clear any previous error/disconnect notifications
                notifications.hide('collab-error');
                notifications.hide('collab-disconnect');
            }
        },
        [onCollabStatusChangeProp]
    );

    const exportDocx = useCallback(() => {
        exportDocX(adapter.getMarkdown());
    }, [adapter, exportDocX]);

    return (
        <EditorAdapterProvider adapter={adapter}>
            <DocumentProvider
                document={data}
                getCurrentMarkdown={() => adapter.getMarkdown()}
            >
                <MilkdownProvider>
                    <ProsemirrorAdapterProvider>
                        <Box
                            className={dark ? 'milkdown-dark' : ''}
                            style={{
                                position: 'relative',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {!readOnly && (
                                <MilkdownToolbar
                                    adapter={adapter}
                                    exportDocx={exportDocx}
                                    openBpmnGenerator={openBpmnGenerator}
                                    userIdentity={userIdentity}
                                    documentContent={data.content}
                                    getCurrentMarkdown={() => adapter.getMarkdown()}
                                    collabStatus={collabStatus}
                                    peerCount={peerCount}
                                    autoSaveStatus={autoSaveStatus}
                                    sourceView={sourceView}
                                    onToggleSourceView={toggleSourceView}
                                />
                            )}
                            {sourceView && (
                                <Textarea
                                    value={sourceText}
                                    onChange={(e) => setSourceText(e.currentTarget.value)}
                                    autosize
                                    minRows={20}
                                    styles={{
                                        input: {
                                            fontFamily: 'monospace',
                                            fontSize: 'var(--mantine-font-size-sm)',
                                            border: 'none',
                                            borderRadius: 0,
                                        }
                                    }}
                                    style={{ flex: 1, padding: '1rem' }}
                                />
                            )}
                            <Box
                                className="milkdown-editor-container"
                                style={{
                                    flex: 1,
                                    padding: '1rem',
                                    display: sourceView ? 'none' : undefined
                                }}
                            >
                                <MilkdownEditorInner
                                    adapter={adapter}
                                    migratedContent={migratedContent}
                                    readOnly={readOnly}
                                    onDocChanged={handleDocChanged}
                                    collabConfig={collabConfig}
                                    onCollabStatusChange={handleCollabStatusChange}
                                    onPeerCountChange={setPeerCount}
                                />
                            </Box>
                        </Box>
                    </ProsemirrorAdapterProvider>
                </MilkdownProvider>
            </DocumentProvider>
        </EditorAdapterProvider>
    );
};

MilkdownEditor.displayName = 'MilkdownEditor';
export default MilkdownEditor;
